(function () {
  if (customElements.get('ncb-special-order')) return;

  class NcbSpecialOrder extends HTMLElement {
    connectedCallback() {
      this.apiBase = this.previewApiBase() || this.dataset.apiBase || '/apps/special-orders/api';
      this.startedAt = Date.now();
      this.offerToken = '';
      this.idempotencyKey = '';
      this.searchRequestId = 0;
      this.checkRequestId = 0;
      this.searchForm = this.querySelector('[data-special-order-search]');
      this.inquiryForm = this.querySelector('[data-special-order-inquiry]');
      this.searchInput = this.querySelector('[data-special-order-query]');
      this.results = this.querySelector('[data-special-order-results]');
      this.resultsList = this.querySelector('[data-special-order-results-list]');
      this.resultsHeading = this.querySelector('[data-special-order-results-heading]');
      this.resultsIntro = this.querySelector('[data-special-order-results-intro]');
      this.status = this.querySelector('[data-special-order-status]');
      this.error = this.querySelector('[data-special-order-error]');
      this.decision = this.querySelector('[data-special-order-decision]');
      this.formPanel = this.querySelector('[data-special-order-form-panel]');
      this.selectedBook = this.querySelector('[data-special-order-selected-book]');
      this.success = this.querySelector('[data-special-order-success]');
      this.requestNumber = this.querySelector('[data-special-order-request-number]');
      this.searchButton = this.querySelector('[data-special-order-search-button]');
      this.submitButton = this.querySelector('[data-special-order-submit-button]');

      const query = new URLSearchParams(window.location.search).get('q');
      if (query && !this.searchInput.value) this.searchInput.value = query.slice(0, 200);

      this.searchForm.addEventListener('submit', this.handleSearch.bind(this));
      this.inquiryForm.addEventListener('submit', this.handleInquiry.bind(this));
    }

    previewApiBase() {
      if (!['127.0.0.1', 'localhost'].includes(window.location.hostname)) return '';
      const value = new URLSearchParams(window.location.search).get('special_orders_api');
      if (!value) return '';
      try {
        const url = new URL(value);
        if (!['127.0.0.1', 'localhost'].includes(url.hostname)) return '';
        if (!['http:', 'https:'].includes(url.protocol)) return '';
        return url.href.replace(/\/$/, '');
      } catch (error) {
        return '';
      }
    }

    capture(name, properties) {
      if (!window.ncbStorefrontAnalytics) return;
      window.ncbStorefrontAnalytics.capture(name, properties || {});
    }

    copy(key, fallback) {
      return this.dataset[key] || fallback || '';
    }

    safeProductUrl(value) {
      if (typeof value !== 'string' || !value.trim()) return '';
      try {
        const url = new URL(value, window.location.origin);
        const path = url.pathname.replace(/\/$/, '');
        if (url.origin !== window.location.origin || url.username || url.password) return '';
        if (!/^\/products\/[a-z0-9][a-z0-9-]*$/i.test(path)) return '';
        return path;
      } catch (error) {
        return '';
      }
    }

    safeCoverUrl(value) {
      if (typeof value !== 'string' || !value.trim()) return '';
      try {
        const url = new URL(value);
        if (url.protocol !== 'https:' || url.username || url.password) return '';
        return url.href;
      } catch (error) {
        return '';
      }
    }

    safeAssetUrl(value) {
      if (typeof value !== 'string' || !value.trim()) return '';
      try {
        const url = new URL(value, window.location.origin);
        const isLocalPreview = ['127.0.0.1', 'localhost'].includes(url.hostname);
        if ((!isLocalPreview && url.protocol !== 'https:') || url.username || url.password) return '';
        return url.href;
      } catch (error) {
        return '';
      }
    }

    limitedCandidates(candidates) {
      return Array.isArray(candidates) ? candidates.slice(0, 8) : [];
    }

    normalizedWorkValue(value) {
      return String(value || '').trim().toLowerCase().replace(/\s+/g, ' ');
    }

    groupedCandidates(candidates) {
      const groups = [];
      const groupsByKey = new Map();
      this.limitedCandidates(candidates).forEach((candidate, index) => {
        const title = String(candidate.title || '').trim();
        const author = String(candidate.author || '').trim();
        const normalizedTitle = this.normalizedWorkValue(title);
        const normalizedAuthor = this.normalizedWorkValue(author);
        const key = normalizedTitle || normalizedAuthor
          ? `${normalizedTitle}\u0000${normalizedAuthor}`
          : `candidate-${candidate.candidate_id || index}`;
        let group = groupsByKey.get(key);
        if (!group) {
          group = { title, author, candidates: [] };
          groupsByKey.set(key, group);
          groups.push(group);
        }
        group.candidates.push(candidate);
      });
      return groups;
    }

    candidateCountCopy(visibleCount, totalCount = visibleCount) {
      if (totalCount > visibleCount) {
        return this.copy('showingEditions', 'Showing %visible% of %total% editions.')
          .replace('%visible%', String(visibleCount))
          .replace('%total%', String(totalCount));
      }
      if (visibleCount === 1) return this.copy('editionFound', '1 edition found.');
      return this.copy('editionsFound', '%count% editions found.').replace('%count%', String(visibleCount));
    }

    candidatePrice(value) {
      const price = String(value || '').trim();
      if (!price) return '';
      const canadianPrice = price.match(/^\$?\s*(\d+(?:,\d{3})*(?:\.\d{1,2})?)\s*(?:CAD)?$/i);
      if (!canadianPrice) return '';
      const amount = Number(canadianPrice[1].replace(/,/g, ''));
      return Number.isFinite(amount) && amount > 0 ? `$${canadianPrice[1]} CAD` : '';
    }

    candidateCover(candidate) {
      const cover = document.createElement('img');
      const placeholderUrl = this.safeAssetUrl(this.copy('coverPlaceholderUrl'));
      const applyPlaceholder = () => {
        if (!placeholderUrl || cover.dataset.placeholder === 'true') {
          cover.hidden = true;
          return;
        }
        cover.dataset.placeholder = 'true';
        cover.classList.add('ncb-special-order__cover--placeholder');
        cover.src = placeholderUrl;
      };
      cover.className = 'ncb-special-order__cover';
      cover.alt = '';
      cover.loading = 'lazy';
      cover.referrerPolicy = 'no-referrer';
      cover.addEventListener('error', applyPlaceholder);
      const coverUrl = this.safeCoverUrl(candidate.cover_url);
      if (coverUrl) {
        cover.src = coverUrl;
      } else {
        applyPlaceholder();
      }
      return cover;
    }

    async post(path, payload) {
      const response = await fetch(`${this.apiBase}/${path}`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      let data = {};
      try {
        data = await response.json();
      } catch (error) {}
      if (!response.ok) {
        const detail = data.detail || data;
        const message = typeof detail === 'string' ? detail : detail.message;
        const failure = new Error(message || this.copy('genericError'));
        failure.status = response.status;
        failure.state = detail && detail.state;
        failure.guard = detail && detail.guard;
        throw failure;
      }
      return data;
    }

    setBusy(button, busy, busyText) {
      if (!button) return;
      if (busy) {
        button.dataset.idleText = button.textContent;
        button.textContent = busyText;
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
      } else {
        button.textContent = button.dataset.idleText || button.textContent;
        button.disabled = false;
        button.removeAttribute('aria-busy');
      }
    }

    clearError() {
      this.error.hidden = true;
      this.error.textContent = '';
    }

    showError(message) {
      this.error.textContent = message || this.copy('genericError');
      this.error.hidden = false;
      this.error.focus();
    }

    resetDecision() {
      this.offerToken = '';
      this.idempotencyKey = '';
      this.decision.hidden = true;
      this.decision.textContent = '';
      this.formPanel.hidden = true;
      this.success.hidden = true;
    }

    async handleSearch(event) {
      event.preventDefault();
      const query = this.searchInput.value.trim();
      if (!query || !this.searchForm.reportValidity()) return;
      const searchRequestId = ++this.searchRequestId;
      this.checkRequestId += 1;
      this.clearError();
      this.resetDecision();
      this.results.hidden = true;
      this.resultsList.replaceChildren();
      this.status.textContent = this.copy('searching');
      this.setBusy(this.searchButton, true, this.copy('searching'));
      this.capture('special_order_search_submitted', { funnel_state: 'submitted' });
      try {
        const data = await this.post('search', {
          query: query,
          website: this.searchForm.elements.website.value,
          started_at_ms: this.startedAt,
        });
        if (searchRequestId !== this.searchRequestId) return;
        this.renderCandidates(data.candidates || []);
        this.capture('special_order_search_completed', {
          funnel_state: data.candidates && data.candidates.length ? 'results' : 'empty',
        });
      } catch (error) {
        if (searchRequestId !== this.searchRequestId) return;
        this.status.textContent = '';
        this.showError(error.message);
        this.capture('special_order_error', {
          funnel_state: 'search', error_category: String(error.status || 'network'),
          error_guard: String(error.guard || ''),
        });
        if (error.state === 'disabled') this.maintenanceDisabled = true;
      } finally {
        if (searchRequestId !== this.searchRequestId) return;
        if (this.maintenanceDisabled) {
          this.searchButton.textContent = this.searchButton.dataset.idleText || this.searchButton.textContent;
          this.searchButton.disabled = true;
          this.searchButton.removeAttribute('aria-busy');
        } else {
          this.setBusy(this.searchButton, false);
        }
      }
    }

    renderCandidates(candidates) {
      const visibleCandidates = this.limitedCandidates(candidates);
      const totalCandidates = Array.isArray(candidates) ? candidates.length : 0;
      this.resultsList.replaceChildren();
      this.results.hidden = false;
      if (!visibleCandidates.length) {
        this.status.textContent = this.copy('noResults');
        this.resultsHeading.textContent = this.copy('noResultsHeading');
        this.resultsIntro.hidden = true;
        this.resultsHeading.focus();
        return;
      }
      this.resultsIntro.hidden = false;
      this.status.textContent = this.candidateCountCopy(visibleCandidates.length, totalCandidates);
      this.resultsHeading.textContent = this.copy('resultsHeading');
      this.groupedCandidates(visibleCandidates).forEach((group) => {
        const groupItem = document.createElement('li');
        groupItem.className = 'ncb-special-order__result-group';
        const groupHeader = document.createElement('div');
        groupHeader.className = 'ncb-special-order__result-group-header';
        const work = document.createElement('div');
        const title = document.createElement('h3');
        title.className = 'ncb-special-order__result-title';
        title.textContent = group.title;
        work.append(title);
        if (group.author) {
          const author = document.createElement('p');
          author.className = 'ncb-special-order__result-author';
          author.textContent = `by ${group.author}`;
          work.append(author);
        }
        const count = document.createElement('span');
        count.className = 'ncb-special-order__result-count';
        count.textContent = group.candidates.length === 1 ? '1 edition' : `${group.candidates.length} editions`;
        groupHeader.append(work, count);

        const editionList = document.createElement('ul');
        editionList.className = 'ncb-special-order__edition-list';
        group.candidates.forEach((candidate, candidateIndex) => {
          const editionItem = document.createElement('li');
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'ncb-special-order__candidate';
          button.setAttribute('aria-pressed', 'false');
          const editionName = [candidate.publisher, candidate.format].filter(Boolean).join(' · ') || 'Edition';
          const price = this.candidatePrice(candidate.list_price);
          const ariaLabel = [
            this.copy('checkEdition', 'Check this edition'),
            `Edition ${candidateIndex + 1}`,
            editionName,
            price,
            group.title ? `of ${group.title}` : '',
            group.author ? `by ${group.author}` : '',
          ].filter(Boolean).join(', ');
          button.setAttribute('aria-label', ariaLabel);
          button.addEventListener('click', () => this.checkCandidate(candidate, button));

          const cover = this.candidateCover(candidate);
          const copy = document.createElement('span');
          copy.className = 'ncb-special-order__candidate-copy';
          const publisher = document.createElement('span');
          publisher.className = 'ncb-special-order__candidate-publisher';
          publisher.textContent = candidate.publisher || candidate.format || `Edition ${candidateIndex + 1}`;
          copy.append(publisher);
          if (candidate.format && candidate.publisher) {
            const format = document.createElement('span');
            format.className = 'ncb-special-order__candidate-format';
            format.textContent = candidate.format;
            copy.append(format);
          }
          if (price) {
            const priceElement = document.createElement('span');
            priceElement.className = 'ncb-special-order__candidate-price';
            priceElement.textContent = price;
            copy.append(priceElement);
          }
          const action = document.createElement('span');
          action.className = 'ncb-special-order__candidate-action';
          action.textContent = this.copy('checkEdition', 'Check this edition');
          copy.append(action);
          button.append(cover, copy);
          editionItem.append(button);
          editionList.append(editionItem);
        });
        groupItem.append(groupHeader, editionList);
        this.resultsList.append(groupItem);
      });
      this.resultsHeading.focus();
    }

    async checkCandidate(candidate, button) {
      const searchRequestId = this.searchRequestId;
      const checkRequestId = ++this.checkRequestId;
      const isCurrentRequest = () => (
        searchRequestId === this.searchRequestId && checkRequestId === this.checkRequestId
      );
      this.clearError();
      this.resetDecision();
      this.resultsList.querySelectorAll('.ncb-special-order__candidate').forEach((element) => {
        element.setAttribute('aria-pressed', String(element === button));
        element.disabled = true;
      });
      this.status.textContent = this.copy('checking');
      this.capture('special_order_candidate_selected', { funnel_state: 'checking' });
      try {
        const data = await this.post('check', {
          candidate_id: candidate.candidate_id,
          website: this.searchForm.elements.website.value,
        });
        if (!isCurrentRequest()) return;
        this.renderDecision(data, candidate);
        this.capture('special_order_availability_checked', { funnel_state: data.state || 'unknown' });
      } catch (error) {
        if (!isCurrentRequest()) return;
        this.status.textContent = '';
        this.showError(error.message);
        this.capture('special_order_error', {
          funnel_state: 'check', error_category: String(error.status || 'network'),
          error_guard: String(error.guard || ''),
        });
      } finally {
        if (!isCurrentRequest()) return;
        this.resultsList.querySelectorAll('.ncb-special-order__candidate').forEach((element) => {
          element.disabled = false;
        });
      }
    }

    renderDecision(data, candidate) {
      this.status.textContent = '';
      this.decision.hidden = false;
      this.decision.replaceChildren();
      const message = document.createElement('span');
      message.textContent = data.message || this.copy('genericError');
      this.decision.append(message);
      const productUrl = this.safeProductUrl(data.product_url);
      if (data.state === 'listed' && productUrl) {
        const link = document.createElement('a');
        link.className = 'ncb-special-order__catalogue-link';
        link.href = productUrl;
        link.textContent = this.copy('viewProduct');
        this.decision.append(document.createElement('br'), link);
      }
      if (data.state !== 'eligible') {
        this.decision.setAttribute('tabindex', '-1');
        this.decision.focus();
        return;
      }
      this.offerToken = data.offer_token;
      this.idempotencyKey = this.idempotencyKey || this.newIdempotencyKey();
      const book = data.book || candidate;
      this.selectedBook.textContent = [
        book.title,
        book.author,
        book.format,
        book.isbn,
      ].filter(Boolean).join(' · ');
      this.formPanel.hidden = false;
      this.formPanel.setAttribute('tabindex', '-1');
      this.formPanel.focus();
    }

    newIdempotencyKey() {
      if (window.crypto && typeof window.crypto.randomUUID === 'function') {
        return window.crypto.randomUUID();
      }
      return `${Date.now()}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
    }

    async handleInquiry(event) {
      event.preventDefault();
      if (!this.offerToken || !this.inquiryForm.reportValidity()) return;
      this.clearError();
      this.setBusy(this.submitButton, true, this.copy('submitting'));
      this.capture('special_order_inquiry_submitted', { funnel_state: 'submitted' });
      const values = new FormData(this.inquiryForm);
      try {
        const data = await this.post('inquiries', {
          offer_token: this.offerToken,
          idempotency_key: this.idempotencyKey,
          name: String(values.get('name') || ''),
          email: String(values.get('email') || ''),
          phone: String(values.get('phone') || ''),
          fulfilment_preference: String(values.get('fulfilment_preference') || ''),
          acknowledgement: values.get('acknowledgement') === 'on',
          website: String(values.get('website') || ''),
          started_at_ms: this.startedAt,
        });
        this.formPanel.hidden = true;
        this.decision.hidden = true;
        this.requestNumber.textContent = data.request_id;
        this.success.hidden = false;
        this.success.focus();
        this.capture('special_order_inquiry_accepted', { funnel_state: 'accepted' });
      } catch (error) {
        this.showError(error.message);
        this.capture('special_order_error', {
          funnel_state: 'inquiry', error_category: String(error.status || 'network'),
          error_guard: String(error.guard || ''),
        });
      } finally {
        this.setBusy(this.submitButton, false);
      }
    }
  }

  customElements.define('ncb-special-order', NcbSpecialOrder);
})();
